import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	CreateDealBody,
	Deal,
	ListDealsParams,
	ListDealsResponse,
	UpdateDealBody,
	UpdateDealStageBody,
	UpdateDealStatusBody,
} from './types/deal'

export const dealsApi = createApi({
	reducerPath: 'dealsApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['Deal'],
	endpoints: (builder) => ({
		listDeals: builder.query<ListDealsResponse, ListDealsParams | void>({
			query: (params) => ({ url: '/crm/deals', params: params ?? {} }),
			providesTags: (result) =>
				result
					? [
							...result.items.map((d) => ({ type: 'Deal' as const, id: d.id })),
							{ type: 'Deal' as const, id: 'LIST' },
						]
					: [{ type: 'Deal' as const, id: 'LIST' }],
		}),

		getDeal: builder.query<Deal | null, string>({
			query: (id) => `/crm/deals/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'Deal', id }],
		}),

		createDeal: builder.mutation<Deal | null, CreateDealBody>({
			query: (body) => ({ url: '/crm/deals', method: 'POST', body }),
			invalidatesTags: [{ type: 'Deal', id: 'LIST' }],
		}),

		updateDeal: builder.mutation<Deal | null, { id: string; body: UpdateDealBody }>({
			query: ({ id, body }) => ({ url: `/crm/deals/${id}`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Deal', id },
				{ type: 'Deal', id: 'LIST' },
			],
		}),

		updateDealStage: builder.mutation<Deal | null, { id: string; body: UpdateDealStageBody }>({
			query: ({ id, body }) => ({ url: `/crm/deals/${id}/stage`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Deal', id },
				{ type: 'Deal', id: 'LIST' },
			],
		}),

		updateDealStatus: builder.mutation<Deal | null, { id: string; body: UpdateDealStatusBody }>({
			query: ({ id, body }) => ({ url: `/crm/deals/${id}/status`, method: 'PATCH', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'Deal', id },
				{ type: 'Deal', id: 'LIST' },
			],
		}),

		deleteDeal: builder.mutation<OkResponse, string>({
			query: (id) => ({ url: `/crm/deals/${id}`, method: 'DELETE' }),
			invalidatesTags: (_r, _e, id) => [
				{ type: 'Deal', id },
				{ type: 'Deal', id: 'LIST' },
			],
		}),
	}),
})

export const {
	useListDealsQuery,
	useGetDealQuery,
	useCreateDealMutation,
	useUpdateDealMutation,
	useUpdateDealStageMutation,
	useUpdateDealStatusMutation,
	useDeleteDealMutation,
} = dealsApi
